import { ChangeEvent, FC, useState } from "react";
import { useAtomValue } from "jotai";

import { TaskDriverStatusEnum } from "@/domain/models/common";
import { ServicesType } from "@/domain/models/job";

import { useUpdateServiceMutation } from "../data/mutations/serviceMutations";
import { useUpdateTaskMutation } from "../data/mutations/taskMutations";
import { useServiceQuery } from "../data/queries/serviceQueries";
import { JobServiceTaskBaseType } from "../domain/task";
import { isDriverReviewingTaskAtom } from "../stores/atoms";
import { DriverScreenWithTextInput } from "./DriverScreenWithTextInput";

const DriverFillingFloorsCleaned: FC<JobServiceTaskBaseType> = ({
  jobId,
  serviceId,
  taskId,
}) => {
  const { data: serviceData } = useServiceQuery({ jobId, serviceId });

  const [floorsCleaned, setFloorsCleaned] = useState<
    number | string | undefined
  >(serviceData?.floorsCleaned ?? undefined);

  const isDriverReviewingTask = useAtomValue(isDriverReviewingTaskAtom);

  const {
    mutate: updateService,
    isPending: isUpdatingService,
    variables,
  } = useUpdateServiceMutation();
  const { mutate: updateTask, isPending: isUpdatingTask } =
    useUpdateTaskMutation();

  const onNextClick = () => {
    const driverFlowStatus = isDriverReviewingTask
      ? TaskDriverStatusEnum.TASK_REVIEW
      : TaskDriverStatusEnum.FILLING_SQFT_PER_FLOOR;

    const serviceParams: Partial<ServicesType> = {
      jobId,
      driverFlowStatus,
    };

    if (floorsCleaned !== undefined) {
      serviceParams.floorsCleaned = floorsCleaned;
    }

    updateService({
      serviceId,
      ...serviceParams,
    });

    updateTask({
      jobId,
      serviceId,
      taskId,
      driverFlowStatus,
    });
  };

  const isLoading = isUpdatingService || isUpdatingTask;

  const isNextButtonLoading =
    isLoading &&
    variables?.driverFlowStatus === TaskDriverStatusEnum.FILLING_SQFT_PER_FLOOR;
  const isPreviousButtonLoading =
    isLoading &&
    variables?.driverFlowStatus ===
      TaskDriverStatusEnum.FILLING_INTERIOR_CLEANUP_TYPE;

  const goToPreviousStep = () => {
    updateService({
      jobId,
      serviceId,
      driverFlowStatus: TaskDriverStatusEnum.FILLING_INTERIOR_CLEANUP_TYPE,
    });

    updateTask({
      jobId,
      serviceId,
      taskId,
      driverFlowStatus: TaskDriverStatusEnum.FILLING_INTERIOR_CLEANUP_TYPE,
    });
  };

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFloorsCleaned(Number(event.target.value));
  };

  return (
    <DriverScreenWithTextInput
      jobId={jobId}
      serviceId={serviceId}
      taskId={taskId}
      title="driver_filling_number_of_floors_cleaned.title"
      inputProps={{
        value: floorsCleaned,
        onChange: handleOnChange,
        inputMode: "numeric",
      }}
      bottomButtonProps={{
        isNextButtonLoading,
        nextButtonTitle: isDriverReviewingTask ? "review" : "next",
        onNextButtonClick: onNextClick,
        isNextButtonDisabled: floorsCleaned === undefined,
        previousButtonTitle:
          "driver_filling_number_of_floors_cleaned.back_button",
        onPreviousButtonClick: isDriverReviewingTask
          ? undefined
          : () => goToPreviousStep(),
        isPreviousButtonLoading,
      }}
    />
  );
};

export { DriverFillingFloorsCleaned };
